import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  ArrowLeft,
  Edit,
  ShieldOff,
  ShieldCheck,
  KeyRound,
  Trash2,
  Building2,
  CalendarCheck,
  ReceiptText,
  ClipboardList,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import { ActivityFeed } from "@/components/admin/ActivityFeed";
import type { AuditLogItem } from "@/components/admin/ActivityFeed";
import {
  getUserById,
  getUserBookings,
  getUserBuildings,
  getUserBillingHistory,
  getUserAuditTrail,
} from "@/lib/admin/queries";
import {
  suspendUser,
  unsuspendUser,
  deleteUser,
  resetUserPassword,
  updateUser,
} from "@/lib/admin/actions";
import type { Profile, Booking, Organization } from "@/types/database";
import { useAuth } from "@/contexts/AuthContext";
import type { DataTableColumn } from "@/components/admin/DataTable";

type EditFormValues = {
  full_name: string;
  email: string;
  role: string;
};

type ConfirmActionType = "suspend" | "unsuspend" | "delete" | "reset" | null;

function UserAvatar({ user }: { user: Profile }) {
  const initials = user.full_name
    ? user.full_name
        .split(" ")
        .map((p) => p[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : user.email[0].toUpperCase();

  return (
    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xl shrink-0">
      {user.avatar_url ? (
        <img
          src={user.avatar_url}
          alt={user.full_name}
          className="h-16 w-16 rounded-full object-cover"
        />
      ) : (
        initials
      )}
    </div>
  );
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";

  const defaultTab = searchParams.get("tab") ?? "overview";

  const [editOpen, setEditOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmActionType>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [bookingsPage, setBookingsPage] = useState(1);
  const [auditPage, setAuditPage] = useState(1);

  const userQuery = useQuery({
    queryKey: ["admin", "user", id],
    queryFn: () => getUserById(id!),
    enabled: !!id,
  });

  const bookingsQuery = useQuery({
    queryKey: ["admin", "user-bookings", id, bookingsPage],
    queryFn: () => getUserBookings(id!, bookingsPage),
    enabled: !!id,
  });

  const buildingsQuery = useQuery({
    queryKey: ["admin", "user-buildings", id],
    queryFn: () => getUserBuildings(id!),
    enabled: !!id,
  });

  const billingQuery = useQuery({
    queryKey: ["admin", "user-billing", id],
    queryFn: () => getUserBillingHistory(id!),
    enabled: !!id,
  });

  const auditQuery = useQuery({
    queryKey: ["admin", "user-audit", id, auditPage],
    queryFn: () => getUserAuditTrail(id!, auditPage),
    enabled: !!id,
  });

  const user = userQuery.data?.data;

  const editForm = useForm<EditFormValues>();
  useEffect(() => {
    if (user) {
      editForm.reset({ full_name: user.full_name, email: user.email, role: user.role });
    }
  }, [user, editForm]);

  const updateMutation = useMutation({
    mutationFn: (data: EditFormValues) => updateUser(id!, data, actorId),
    onSuccess: () => {
      toast.success("User updated");
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
    },
    onError: () => toast.error("Failed to update user"),
  });

  async function handleConfirm() {
    if (!confirmAction || !user) return;
    setConfirmLoading(true);
    try {
      if (confirmAction === "suspend") {
        const res = await suspendUser(id!, actorId);
        if (res.success) {
          toast.success("User suspended");
          queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
        } else {
          toast.error(res.error ?? "Failed");
        }
      } else if (confirmAction === "unsuspend") {
        const res = await unsuspendUser(id!, actorId);
        if (res.success) {
          toast.success("User unsuspended");
          queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
        } else {
          toast.error(res.error ?? "Failed");
        }
      } else if (confirmAction === "delete") {
        const res = await deleteUser(id!, actorId);
        if (res.success) {
          toast.success("User deleted");
          navigate("/admin/users");
        } else {
          toast.error(res.error ?? "Failed");
        }
      } else if (confirmAction === "reset") {
        const res = await resetUserPassword(user.email);
        if (res.success) {
          toast.success("Password reset email sent");
        } else {
          toast.error(res.error ?? "Failed");
        }
      }
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
    }
  }

  const bookingColumns: DataTableColumn<Booking & Record<string, unknown>>[] = [
    {
      key: "check_in",
      label: "Check-In",
      render: (row) => (
        <span className="text-sm tabular-nums">
          {new Date(row.check_in as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "check_out",
      label: "Check-Out",
      render: (row) => (
        <span className="text-sm tabular-nums">
          {new Date(row.check_out as string).toLocaleDateString("en-CA")}
        </span>
      ),
    },
    {
      key: "unit",
      label: "Unit",
      render: (row) => {
        const unit = row.unit as { unit_number?: string } | undefined;
        return <span className="text-sm">{unit?.unit_number ?? "—"}</span>;
      },
    },
    {
      key: "nights",
      label: "Nights",
      render: (row) => (
        <span className="text-sm tabular-nums">{row.nights as number}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status as string} size="sm" />,
    },
  ];

  const buildingColumns: DataTableColumn<Organization & Record<string, unknown>>[] = [
    {
      key: "name",
      label: "Building",
      render: (row) => (
        <div>
          <p className="font-medium text-gray-900">{row.name as string}</p>
          <p className="text-xs text-gray-400">{(row.address as string | null) ?? "—"}</p>
        </div>
      ),
    },
    {
      key: "plan",
      label: "Plan",
      render: (row) => (
        <Badge variant="outline" className="text-xs capitalize">
          {(row.plan as string).replace(/_/g, " ")}
        </Badge>
      ),
    },
    {
      key: "total_units",
      label: "Units",
      render: (row) => (
        <span className="text-sm tabular-nums">{row.total_units as number}</span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/buildings/${row.id as string}`)}
        >
          View
        </Button>
      ),
    },
  ];

  const auditItems: AuditLogItem[] = (auditQuery.data?.data ?? []).map((item) => ({
    id: item.id,
    action: item.action,
    entity_type: item.entity ?? "",
    entity_id: "",
    actor_name: item.user_name ?? "System",
    created_at: item.created_at,
    details: null,
  }));

  if (userQuery.isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="text-center py-20 text-gray-400">User not found.</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      {/* Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <UserAvatar user={user} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900">{user.full_name}</h2>
                <StatusBadge status="active" size="sm" />
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {user.role.replace(/_/g, " ")}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
              {user.phone && (
                <p className="text-sm text-gray-500">{user.phone}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="whitespace-nowrap" onClick={() => setEditOpen(true)}>
                <Edit className="h-4 w-4 mr-1.5" /> Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction("suspend")}
              >
                <ShieldOff className="h-4 w-4 mr-1.5" /> Suspend
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction("unsuspend")}
              >
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Unsuspend
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction("reset")}
              >
                <KeyRound className="h-4 w-4 mr-1.5" /> Reset Password
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="whitespace-nowrap"
                onClick={() => setConfirmAction("delete")}
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue={defaultTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="whitespace-nowrap">
            <UserIcon className="h-4 w-4 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="bookings" className="whitespace-nowrap">
            <CalendarCheck className="h-4 w-4 mr-1.5" /> Bookings
          </TabsTrigger>
          <TabsTrigger value="buildings" className="whitespace-nowrap">
            <Building2 className="h-4 w-4 mr-1.5" /> Buildings
          </TabsTrigger>
          <TabsTrigger value="billing" className="whitespace-nowrap">
            <ReceiptText className="h-4 w-4 mr-1.5" /> Billing
          </TabsTrigger>
          <TabsTrigger value="audit" className="whitespace-nowrap">
            <ClipboardList className="h-4 w-4 mr-1.5" /> Audit Trail
          </TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Profile Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                {[
                  { label: "User ID", value: user.id },
                  { label: "Full Name", value: user.full_name },
                  { label: "Email", value: user.email },
                  { label: "Phone", value: user.phone ?? "—" },
                  { label: "Role", value: user.role.replace(/_/g, " ") },
                  {
                    label: "Joined",
                    value: new Date(user.created_at).toLocaleDateString("en-CA", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }),
                  },
                  {
                    label: "Last Updated",
                    value: new Date(user.updated_at).toLocaleDateString("en-CA", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }),
                  },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {label}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 break-all">{value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bookings */}
        <TabsContent value="bookings">
          <Card>
            <CardHeader>
              <CardTitle>Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={bookingColumns as DataTableColumn[]}
                data={(bookingsQuery.data?.data ?? []) as Record<string, unknown>[]}
                loading={bookingsQuery.isLoading}
                totalCount={bookingsQuery.data?.count}
                page={bookingsPage}
                pageSize={20}
                onPageChange={setBookingsPage}
                emptyMessage="No bookings found for this user."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Buildings */}
        <TabsContent value="buildings">
          <Card>
            <CardHeader>
              <CardTitle>Assigned Buildings</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={buildingColumns as DataTableColumn[]}
                data={(buildingsQuery.data?.data ?? []) as Record<string, unknown>[]}
                loading={buildingsQuery.isLoading}
                emptyMessage="No buildings assigned to this user."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Billing &amp; Subscription</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {billingQuery.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : (billingQuery.data?.data ?? []).length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">
                  No billing history found.
                </div>
              ) : (
                <div className="space-y-3">
                  {(billingQuery.data?.data ?? []).map((sub: { id: string; price_id?: string; created_at: string; trial_end?: string; current_period_end?: string; status: string }) => (
                    <div
                      key={sub.id}
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {(sub.price_id ?? "Unknown plan")
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </p>
                        <p className="text-xs text-gray-400">
                          Since{" "}
                          {new Date(sub.created_at).toLocaleDateString("en-CA")}
                          {sub.trial_end && (
                            <> · Trial ends {new Date(sub.trial_end).toLocaleDateString("en-CA")}</>
                          )}
                          {sub.current_period_end && (
                            <> · Renews {new Date(sub.current_period_end).toLocaleDateString("en-CA")}</>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={sub.status} size="sm" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Trail */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Trail</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityFeed
                items={auditItems}
                loading={auditQuery.isLoading}
                maxItems={50}
              />
              {(auditQuery.data?.count ?? 0) > 20 && (
                <div className="flex justify-center mt-4 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                    disabled={auditPage <= 1}
                    onClick={() => setAuditPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                    disabled={(auditQuery.data?.data ?? []).length < 20}
                    onClick={() => setAuditPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-[400px] sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
          </SheetHeader>
          <form
            onSubmit={editForm.handleSubmit((data) => updateMutation.mutate(data))}
            className="mt-6 space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="eu-name" className="whitespace-nowrap">Full Name <span className="text-red-500">*</span></Label>
              <Input id="eu-name" {...editForm.register("full_name", { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eu-email" className="whitespace-nowrap">Email <span className="text-red-500">*</span></Label>
              <Input
                id="eu-email"
                type="email"
                {...editForm.register("email", { required: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eu-role" className="whitespace-nowrap">Role</Label>
              <Select
                value={editForm.watch("role")}
                onValueChange={(v) => editForm.setValue("role", v)}
              >
                <SelectTrigger id="eu-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="host">Host</SelectItem>
                  <SelectItem value="strata_admin">Strata Admin</SelectItem>
                  <SelectItem value="property_manager">Property Manager</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="system_admin">System Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setEditOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Confirm Modal */}
      <ConfirmModal
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        loading={confirmLoading}
        title={
          confirmAction === "delete"
            ? "Delete User"
            : confirmAction === "suspend"
            ? "Suspend User"
            : confirmAction === "unsuspend"
            ? "Unsuspend User"
            : "Reset Password"
        }
        description={
          confirmAction === "delete"
            ? `Permanently delete ${user.full_name}? All their data will be removed.`
            : confirmAction === "suspend"
            ? `Suspend ${user.full_name}? They will immediately lose platform access.`
            : confirmAction === "unsuspend"
            ? `Restore access for ${user.full_name}?`
            : `Send a password reset email to ${user.email}?`
        }
        requireTyping={confirmAction === "delete"}
        variant={confirmAction === "delete" ? "destructive" : "warning"}
      />
    </div>
  );
}
